/**
 * Stylux Luxury Fashion - Store & Interactivity Engine
 * Replicating exact OuterHTML & CSS Structure of Shopify Custom Cart Drawer:
 * - <dialog ref="panel" class="theme-drawer__dialog color-custom-drawer" ...>
 * - Exactly 30rem width (or 480px, max 100vw) on the right side
 * - Exact table structure, titles, variants, quantity selector, trash remove icon, subtotal, Buy 2 & Save Rs. 500, discount accordion, estimated total, Buy with Cash on Delivery, and Check out
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'thudarum_cart_items';
  const COUPON_KEY = 'thudarum_applied_coupon';
  const DISCOUNTS_STORAGE_KEY = 'stylux_discounts_database';

  const DEFAULT_DISCOUNTS = [
    { code: 'WELCOME10', type: 'percentage', value: 10, active: true, description: '10% Welcome Discount' },
    { code: 'SAVE20', type: 'percentage', value: 20, active: true, description: '20% Special Savings' },
    { code: 'LUXURY15', type: 'percentage', value: 15, active: true, description: '15% Horology Collector Privilege' },
    { code: 'SAVE500', type: 'flat', value: 500, active: true, description: 'Flat Rs. 500 Off' }
  ];

  const DISCOUNTS_INIT_KEY = 'stylux_discounts_initialized';

  function getDiscountsDatabase() {
    if (window.StyluxSharedStore && typeof window.StyluxSharedStore.getDiscounts === 'function') {
      const storeDiscounts = window.StyluxSharedStore.getDiscounts();
      if (Array.isArray(storeDiscounts) && storeDiscounts.length > 0) return storeDiscounts;
    }
    if (window.__STYLUX_SHARED_CONFIG__ && Array.isArray(window.__STYLUX_SHARED_CONFIG__.activeDiscounts) && window.__STYLUX_SHARED_CONFIG__.activeDiscounts.length > 0) {
      return window.__STYLUX_SHARED_CONFIG__.activeDiscounts;
    }
    try {
      const data = localStorage.getItem(DISCOUNTS_STORAGE_KEY) || localStorage.getItem('stylux_active_discounts');
      if (data) {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {}
    return DEFAULT_DISCOUNTS;
  }

  function saveDiscountsDatabase(discounts) {
    if (window.StyluxSharedStore && typeof window.StyluxSharedStore.setDiscounts === 'function') {
      window.StyluxSharedStore.setDiscounts(discounts);
    } else {
      try {
        localStorage.setItem(DISCOUNTS_STORAGE_KEY, JSON.stringify(discounts));
        localStorage.setItem('stylux_active_discounts', JSON.stringify(discounts));
        localStorage.setItem(DISCOUNTS_INIT_KEY, 'true');
        window.dispatchEvent(new CustomEvent('stylux:discounts_updated', { detail: { discounts } }));
      } catch (e) {}
    }
  }

  window.addEventListener('stylux:discounts_updated', () => {
    try {
      const current = getCoupon();
      if (current) {
        const valid = findDiscountByCode(current.code);
        if (!valid) {
          saveCoupon(null);
        } else {
          saveCoupon({
            code: valid.code,
            type: valid.type,
            value: valid.value,
            description: valid.description || ''
          });
        }
      }
    } catch (e) {}
    updateCartUI();
  });

  function findDiscountByCode(codeStr) {
    if (!codeStr) return null;
    const clean = codeStr.trim().toUpperCase();
    
    // 1. Check primary discounts database
    const discounts = getDiscountsDatabase();
    if (Array.isArray(discounts)) {
      const found = discounts.find(d => d.code && d.code.toUpperCase() === clean && d.active !== false);
      if (found) return found;
    }

    // 2. Check global shared config
    if (window.__STYLUX_SHARED_CONFIG__ && Array.isArray(window.__STYLUX_SHARED_CONFIG__.activeDiscounts)) {
      const found = window.__STYLUX_SHARED_CONFIG__.activeDiscounts.find(d => d.code && d.code.toUpperCase() === clean && d.active !== false);
      if (found) return found;
    }

    // 3. Fallback to default active discounts
    const defaultMatch = DEFAULT_DISCOUNTS.find(d => d.code && d.code.toUpperCase() === clean && d.active !== false);
    if (defaultMatch) return defaultMatch;

    return null;
  }

  // --- 1A. AUTHORITATIVE SHOPIFY STOREFRONT DISCOUNT VALIDATION ---
  async function validateShopifyDiscount(codeStr, currentCart) {
    if (!codeStr) return { valid: false, error: 'Please enter a discount code' };
    const cleanCode = codeStr.trim().toUpperCase();

    // 1. Check Shopify Storefront GraphQL endpoint for real-time validation & exact allocation
    try {
      const lines = [];
      (currentCart || []).forEach(item => {
        const rawVid = item.variantId || (String(item.id).length > 8 && !isNaN(Number(item.id)) ? item.id : null);
        if (rawVid && !isNaN(Number(rawVid))) {
          lines.push({
            merchandiseId: 'gid://shopify/ProductVariant/' + rawVid,
            quantity: parseInt(item.quantity || 1, 10) || 1
          });
        }
      });

      const gqlQuery = JSON.stringify({
        query: `
          mutation cartCreate($input: CartInput!) {
            cartCreate(input: $input) {
              cart {
                id
                discountCodes {
                  code
                  applicable
                }
                discountAllocations {
                  discountedAmount {
                    amount
                    currencyCode
                  }
                }
              }
              userErrors {
                field
                message
                code
              }
            }
          }
        `,
        variables: {
          input: {
            lines: lines,
            discountCodes: [cleanCode]
          }
        }
      });

      const res = await fetch('/api/2024-01/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: gqlQuery
      });

      if (res.ok) {
        const data = await res.json();
        const cartData = data?.data?.cartCreate?.cart;
        if (cartData && Array.isArray(cartData.discountCodes)) {
          const match = cartData.discountCodes.find(d => d.code && d.code.toUpperCase() === cleanCode);
          if (match) {
            if (match.applicable) {
              let discountAmount = 0;
              if (Array.isArray(cartData.discountAllocations) && cartData.discountAllocations.length > 0) {
                cartData.discountAllocations.forEach(alloc => {
                  discountAmount += parseFloat(alloc?.discountedAmount?.amount || 0);
                });
              }
              return {
                valid: true,
                code: cleanCode,
                type: 'flat',
                value: discountAmount > 0 ? discountAmount : 0,
                description: `Shopify Applied Discount (${cleanCode})`
              };
            } else {
              return {
                valid: false,
                error: `Promo code "${cleanCode}" is invalid, inactive, or not applicable to your order`
              };
            }
          }
        }
      }
    } catch(e) {}

    // 2. Also check verified store discount configuration
    const localMatch = findDiscountByCode(cleanCode);
    if (localMatch) {
      return {
        valid: true,
        code: localMatch.code,
        type: localMatch.type,
        value: localMatch.value,
        description: localMatch.description || ''
      };
    }

    return {
      valid: false,
      error: `Promo code "${cleanCode}" is invalid, inactive, or not applicable to your order`
    };
  }

  // --- 1. LOCAL STORAGE HELPERS ---
  function getCart() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      let items = data ? JSON.parse(data) : [];
      if (!Array.isArray(items)) items = [];
      
      // Purge legacy auto-seeded mock items
      if (items.some(i => String(i.id) === '43477479686262' || String(i.id) === '43480799674486' || (i.name && i.name.includes('ROLEX + HUBLOT CUT')))) {
        items = [];
        localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
      }

      return items;
    } catch (e) {
      return [];
    }
  }

  function saveCart(cart) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    } catch (e) {}
    updateCartUI();
  }

  function getCoupon() {
    try {
      const data = localStorage.getItem(COUPON_KEY);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  function saveCoupon(coupon) {
    try {
      if (coupon) {
        localStorage.setItem(COUPON_KEY, JSON.stringify(coupon));
      } else {
        localStorage.removeItem(COUPON_KEY);
      }
    } catch (e) {}
    updateCartUI();
  }

  function formatRs(num) {
    return 'Rs.' + Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function updateCartUI() {
    if (typeof renderSideCartContent === 'function') {
      renderSideCartContent();
    }
  }

  function isMobileDevice() {
    try {
      if (typeof window === 'undefined') return false;
      return (
        window.innerWidth <= 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '') ||
        (window.matchMedia && window.matchMedia('(max-width: 768px)').matches)
      );
    } catch (e) {
      return false;
    }
  }

  // --- 2. CART MUTATIONS & SHOPIFY SYNC ---
  function addToCart(item, openDrawer = true) {
    try { localStorage.setItem('thudarum_user_interacted', 'true'); } catch (e) {}
    const cart = getCart();
    const itemId = String(item.variantId || item.id || Date.now());
    const existingIndex = cart.findIndex((ci) => String(ci.id) === itemId || (item.variantId && String(ci.variantId) === String(item.variantId)));

    if (existingIndex > -1) {
      cart[existingIndex].quantity = (cart[existingIndex].quantity || 1) + (item.quantity || 1);
    } else {
      cart.push({
        id: itemId,
        variantId: item.variantId ? String(item.variantId) : (String(item.id).length > 8 ? String(item.id) : ''),
        name: item.name || item.title || 'Luxury Timepiece',
        variant: item.variant || item.size || 'Standard',
        price: Number(item.price) || 2999,
        originalPrice: Number(item.originalPrice) || Math.round((Number(item.price) || 2999) * 1.3),
        image: item.image || item.featured_image || '',
        quantity: item.quantity || 1
      });
    }

    saveCart(cart);
    if (!isMobileDevice()) {
      showToast('Added to cart', item.name || item.title || 'Product added');
    }

    // Background sync to native Shopify cart session
    const rawVariantId = Number(item.variantId || item.id);
    if (!isNaN(rawVariantId) && rawVariantId > 1000000) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: rawVariantId, quantity: item.quantity || 1 }] })
      }).catch(function() {});
    }

    if (openDrawer) {
      setTimeout(() => {
        openSideCart();
      }, 100);
    }
  }

  function removeFromCart(id) {
    let cart = getCart();
    const removed = cart.find((ci) => String(ci.id) === String(id));
    cart = cart.filter((ci) => String(ci.id) !== String(id));
    saveCart(cart);
    showToast('Item removed', 'Product removed from your cart');

    // Sync with Shopify backend
    const varId = removed ? (removed.variantId || removed.id) : id;
    if (varId && !isNaN(Number(varId))) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: String(varId), quantity: 0 })
      }).catch(function() {});
    }
  }

  function updateItemQuantity(id, delta) {
    const cart = getCart();
    const item = cart.find((ci) => String(ci.id) === String(id));
    if (item) {
      item.quantity = (item.quantity || 1) + delta;
      if (item.quantity <= 0) {
        removeFromCart(id);
        return;
      }
      saveCart(cart);

      // Sync with Shopify backend
      const varId = item.variantId || item.id;
      if (varId && !isNaN(Number(varId))) {
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: String(varId), quantity: item.quantity })
        }).catch(function() {});
      }
    }
  }

  // --- 2B. SHOPIFY TO LOCAL STORAGE AUTOMATIC HYDRATION ---
  function syncCartWithShopify(force = false) {
    fetch('/cart.js')
      .then(r => r.json())
      .then(shopifyCart => {
        if (!shopifyCart || !Array.isArray(shopifyCart.items)) return;
        const current = getCart();

        // If Shopify backend has items but local storage is empty (or force sync requested)
        if (shopifyCart.items.length > 0 && (current.length === 0 || force)) {
          const hydrated = shopifyCart.items.map(si => ({
            id: String(si.variant_id || si.id),
            variantId: String(si.variant_id || si.id),
            name: si.product_title || si.title,
            variant: si.variant_title || 'Standard',
            price: Math.round(si.price / 100),
            originalPrice: Math.round((si.original_price || si.price * 1.3) / 100),
            image: si.image || (si.featured_image ? si.featured_image.url : ''),
            quantity: si.quantity || 1
          }));
          localStorage.setItem(STORAGE_KEY, JSON.stringify(hydrated));
          updateCartUI();
        }
      })
      .catch(function() {});
  }

  // --- 2C. REAL SHOPIFY CHECKOUT REDIRECT (CASH ON DELIVERY) ---
  function proceedToShopifyCheckout(triggerBtn) {
    const currentCart = getCart();
    if (currentCart.length === 0) {
      showToast('Cart is empty', 'Please select a timepiece first.', true);
      return;
    }

    // Visual loading state on the button
    if (triggerBtn) {
      triggerBtn.style.opacity = '0.7';
      triggerBtn.style.pointerEvents = 'none';
      const titleSpan = triggerBtn.querySelector ? triggerBtn.querySelector('.es-button-title') : null;
      if (titleSpan) {
        titleSpan.textContent = 'Redirecting to Checkout...';
      } else if (typeof triggerBtn.textContent === 'string') {
        triggerBtn.textContent = 'Redirecting to Checkout...';
      }
    }

    const coupon = getCoupon();
    const totalCount = currentCart.reduce((sum, item) => sum + (item.quantity || 1), 0);
    
    // Auto-propagate bundle discount (Rs.500 off on 2+ items) or active promo code to Shopify checkout
    let discountCode = '';
    if (coupon && coupon.code) {
      discountCode = coupon.code;
    } else if (totalCount >= 2) {
      discountCode = 'STYLUX500';
    }

    let checkoutUrl = discountCode 
      ? '/discount/' + encodeURIComponent(discountCode) + '?redirect=' + encodeURIComponent('/checkout?discount=' + encodeURIComponent(discountCode))
      : '/checkout';

    // Collect valid Shopify items
    const shopifyItems = [];
    const directCartParams = [];
    currentCart.forEach(item => {
      const rawVid = item.variantId || (String(item.id).length > 8 && !isNaN(Number(item.id)) ? item.id : null);
      const qty = parseInt(item.quantity || 1, 10) || 1;
      if (rawVid && !isNaN(Number(rawVid)) && Number(rawVid) > 100000) {
        shopifyItems.push({ id: parseInt(rawVid, 10), quantity: qty });
        directCartParams.push(rawVid + ':' + qty);
      }
    });

    // Background record in local store (without showing login modal)
    try {
      const rawSubtotal = currentCart.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
      let discountAmount = 0;
      if (coupon) {
        if (coupon.type === 'flat' || (coupon.discount && coupon.discount > 100)) {
          discountAmount += (coupon.value || coupon.discount || 0);
        } else {
          const pct = coupon.value || coupon.discount || 0;
          discountAmount += (rawSubtotal * pct) / 100;
        }
      } else if (totalCount >= 2) {
        discountAmount = 500;
      }
      const finalTotal = Math.max(0, rawSubtotal - discountAmount);
      if (window.StyluxAuth && typeof window.StyluxAuth.createOrderTransaction === 'function') {
        window.StyluxAuth.createOrderTransaction(currentCart, finalTotal, null, discountAmount);
      }
    } catch(e) {}

    // Ensure Shopify backend cart has these items, then navigate to real /checkout
    if (shopifyItems.length > 0) {
      fetch('/cart/clear.js', { method: 'POST' })
        .catch(() => {})
        .finally(() => {
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: shopifyItems })
          })
          .then(() => {
            window.location.href = checkoutUrl;
          })
          .catch(() => {
            if (directCartParams.length > 0) {
              window.location.href = '/cart/' + directCartParams.join(',') + discountQuery;
            } else {
              window.location.href = checkoutUrl;
            }
          });
        });
    } else {
      window.location.href = checkoutUrl;
    }
  }

  // --- 3. TOAST NOTIFICATIONS ---
  function showToast(title, description, isError = false) {
    // Suppress "Added to cart" notifications completely on mobile devices
    if (isMobileDevice() && (title === 'Added to cart' || (typeof title === 'string' && title.toLowerCase().includes('added to cart')))) {
      return;
    }

    let container = document.querySelector('.thudarum-toast-container');
    if (!container) {
      container = document.createElement('ol');
      container.tabIndex = -1;
      container.className = 'thudarum-toast-container fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px] pointer-events-none gap-2 m-0 list-none';
      document.body.appendChild(container);
    }

    const toast = document.createElement('li');
    toast.role = 'status';
    toast.tabIndex = 0;
    toast.className = 'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md p-6 pr-8 shadow-xl border bg-background text-foreground select-none ' + 
      (isError ? 'border-destructive text-destructive' : 'border-border');

    toast.style.transition = 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.35s ease';
    toast.style.transform = 'translate3d(0, 100%, 0) scale(0.95)';
    toast.style.opacity = '0';
    toast.style.willChange = 'transform, opacity';

    toast.innerHTML = `
      <div class="grid gap-1">
        <div class="text-sm font-semibold tracking-tight">${title}</div>
        ${description ? `<div class="text-sm opacity-90 text-muted-foreground">${description}</div>` : ''}
      </div>
      <button type="button" class="absolute right-2 top-2 rounded-md p-1.5 text-foreground/50 opacity-0 transition-opacity hover:text-foreground hover:bg-accent focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 cursor-pointer" toast-close="" title="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
      </button>
    `;

    let isDismissed = false;
    const removeToast = () => {
      if (isDismissed) return;
      isDismissed = true;
      toast.style.transform = 'translate3d(120%, 0, 0) scale(0.92)';
      toast.style.opacity = '0';
      setTimeout(() => {
        if (toast.parentElement) toast.remove();
      }, 400);
    };

    const closeBtn = toast.querySelector('button[toast-close]');
    if (closeBtn) closeBtn.onclick = removeToast;

    container.appendChild(toast);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toast.style.transform = 'translate3d(0, 0, 0) scale(1)';
        toast.style.opacity = '1';
      });
    });

    setTimeout(() => {
      if (toast.parentElement && !isDismissed) removeToast();
    }, 3200);
  }

  // --- 4. HEADER CART COUNTER UPDATER ---
  function updateCartUI() {
    const cart = getCart();
    const totalCount = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);

    // 1. Desktop header badge
    document.querySelectorAll('.js-desktop-cart-count, .desktop-cart-count').forEach(el => {
      el.textContent = totalCount;
    });
    document.querySelectorAll('.desktop-cart-bubble').forEach(el => {
      el.style.display = totalCount > 0 ? 'block' : 'none';
    });

    // 2. Mobile header badge
    document.querySelectorAll('.js-mobile-cart-count').forEach(el => {
      el.textContent = totalCount;
      el.style.display = 'inline';
    });
    document.querySelectorAll('.mobile-cart-badge').forEach(el => {
      el.textContent = totalCount;
      el.style.display = totalCount > 0 ? 'inline-flex' : 'none';
    });

    // 3. Generic & drawer badges
    document.querySelectorAll('[data-cart-count], .cart-count-badge, #theme-drawer-badge').forEach(el => {
      el.textContent = totalCount;
    });

    // Remove obsolete duplicate badges
    document.querySelectorAll('#header-cart-link > .thudarum-cart-badge, header a > .thudarum-cart-badge, header > .thudarum-cart-badge').forEach(b => b.remove());

    const cartButton = document.getElementById('header-cart-btn');
    if (cartButton) {
      const sr = cartButton.querySelector('.sr-only');
      if (sr) sr.textContent = `Cart (${totalCount} items)`;
    }

    renderSideCartContent();
    if (typeof renderMainCartPage === 'function') {
      renderMainCartPage();
    }

    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { count: totalCount } }));
    } catch(e) {}
  }

  // --- 5. EXACT SHOPIFY THEME DRAWER DIALOG INJECTION ---
  let isSideCartOpen = false;

  function ensureSideCartDom() {
    let root = document.getElementById('stylux-side-cart-root');
    if (root) return root;

    // Inject exact CSS for the theme-drawer
    const styleTag = document.createElement('style');
    styleTag.id = 'theme-drawer-exact-styles';
    styleTag.textContent = `
      .theme-drawer__dialog {
        position: fixed !important;
        top: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        left: auto !important;
        margin: 0 !important;
        width: 30rem !important;
        max-width: 100vw !important;
        height: 100dvh !important;
        max-height: none !important;
        padding: 0 !important;
        border: none !important;
        border-left: 1px solid #DFDFDF !important;
        background-color: #ffffff !important;
        color: #000000 !important;
        display: flex !important;
        flex-direction: column !important;
        box-shadow: -4px 0 25px rgba(0,0,0,0.15) !important;
        z-index: 10000 !important;
        pointer-events: none !important;
        font-family: Poppins, -apple-system, BlinkMacSystemFont, sans-serif !important;
        transform: translate3d(100%, 0, 0) !important;
        transition: transform 0.42s cubic-bezier(0.16, 1, 0.3, 1) !important;
        box-sizing: border-box !important;
        will-change: transform;
      }
      .theme-drawer__dialog[open] {
        display: flex !important;
      }
      .theme-drawer__dialog[open].active {
        transform: translate3d(0, 0, 0) !important;
        pointer-events: auto !important;
      }

      .theme-drawer__grab-handle-bar {
        display: none;
        width: 100%;
        padding: 12px 0 6px 0;
        justify-content: center;
        align-items: center;
        cursor: grab;
        flex-shrink: 0;
        touch-action: none;
        user-select: none;
      }
      .theme-drawer__grab-handle-pill {
        width: 44px;
        height: 5px;
        border-radius: 9999px;
        background-color: #d1d5db;
        transition: background-color 0.2s ease, transform 0.2s ease;
      }
      .theme-drawer__grab-handle-bar:active .theme-drawer__grab-handle-pill {
        background-color: #9ca3af;
        transform: scale(1.08);
      }

      /* Mobile 85% Bottom Sheet with Non-Linear Easing & Drag to Close */
      @media (max-width: 767px) {
        .theme-drawer__grab-handle-bar {
          display: flex !important;
          touch-action: none !important;
        }
        .theme-drawer__dialog {
          top: auto !important;
          bottom: 0 !important;
          left: 0 !important;
          right: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          height: 88vh !important;
          height: 88dvh !important;
          max-height: 88dvh !important;
          border-left: none !important;
          border-top: 1px solid rgba(255, 255, 255, 0.90) !important;
          border-radius: 24px 24px 0 0 !important;
          background: rgba(255, 255, 255, 0.82) !important;
          backdrop-filter: blur(28px) saturate(190%) contrast(102%) brightness(1.04) !important;
          -webkit-backdrop-filter: blur(28px) saturate(190%) contrast(102%) brightness(1.04) !important;
          box-shadow: 0 -16px 48px rgba(15, 23, 42, 0.16), inset 0 1px 1px 0 rgba(255, 255, 255, 1) !important;
          transform: translate3d(0, 100%, 0) !important;
          transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1), height 0.28s cubic-bezier(0.16, 1, 0.3, 1), max-height 0.28s cubic-bezier(0.16, 1, 0.3, 1) !important;
          overflow: hidden !important;
          will-change: transform, height !important;
        }
        .theme-drawer__dialog.is-expanded {
          height: 88vh !important;
          height: 88dvh !important;
          max-height: 88dvh !important;
        }
        .theme-drawer__dialog.is-dragging {
          transition: none !important;
        }
        .theme-drawer__dialog[open].active {
          transform: translate3d(0, 0, 0) !important;
          pointer-events: auto !important;
        }
        .theme-drawer__header {
          background: transparent !important;
          border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
          padding: 0.75rem 1.25rem 0.85rem 1.25rem !important;
          touch-action: pan-y !important;
        }
        .cart-drawer__content {
          background: transparent !important;
        }
        .cart-drawer__summary {
          background: rgba(255, 255, 255, 0.50) !important;
          backdrop-filter: blur(16px) !important;
          -webkit-backdrop-filter: blur(16px) !important;
          border-top: 1px solid rgba(255, 255, 255, 0.70) !important;
        }
      }
      .theme-drawer__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1.25rem 1.5rem;
        border-bottom: 1px solid #DFDFDF;
      }
      .theme-drawer__title {
        font-size: 1.5rem;
        font-weight: 500;
        margin: 0;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      .theme-drawer__badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.5rem;
        height: 1.5rem;
        border-radius: 50%;
        background: #f0f0f0;
        color: #333333;
        font-size: 0.75rem;
        font-weight: 600;
      }
      .theme-drawer__close-button {
        background: transparent;
        border: 1px solid #e5e7eb;
        border-radius: 50%;
        width: 2rem;
        height: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: #6b7280;
        transition: all 0.2s ease;
        position: relative !important;
        z-index: 50 !important;
        pointer-events: auto !important;
        -webkit-tap-highlight-color: transparent !important;
      }
      .theme-drawer__close-button * {
        pointer-events: none !important;
      }
      .theme-drawer__close-button:hover {
        background: #f3f4f6;
        color: #111827;
      }
      .cart-items__table {
        width: 100%;
        border-collapse: collapse;
      }
      .cart-items__table-row {
        border-bottom: 1px solid #f3f4f6;
        display: flex;
        padding: 1.25rem 0;
        gap: 1rem;
        align-items: flex-start;
      }
      .cart-items__media {
        width: 4.5rem;
        height: 5.5rem;
        flex-shrink: 0;
        background: #f9fafb;
        border-radius: 4px;
        overflow: hidden;
      }
      .cart-items__media img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      .cart-items__details {
        flex: 1;
        min-width: 0;
      }
      .cart-items__title {
        font-size: 0.875rem;
        font-weight: 500;
        color: #000000;
        text-decoration: none;
        line-height: 1.35;
        display: block;
        margin-bottom: 0.25rem;
      }
      .cart-items__variants-wrapper {
        font-size: 0.75rem;
        color: #6b7280;
        margin-bottom: 0.35rem;
      }
      .cart-items__unit-price-wrapper {
        font-size: 0.875rem;
        font-weight: 600;
        color: #000000;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      .compare-at-price {
        color: #9ca3af;
        text-decoration: line-through;
        font-weight: 400;
        font-size: 0.8125rem;
      }
      .cart-items__quantity-controls {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-top: 0.75rem;
      }
      .quantity-selector {
        display: inline-flex;
        align-items: center;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        background: #ffffff;
      }
      .quantity-selector button {
        background: transparent;
        border: none;
        padding: 0.35rem 0.55rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #4b5563;
      }
      .quantity-selector button:hover {
        color: #000000;
      }
      .quantity-selector input {
        width: 2rem;
        text-align: center;
        border: none;
        font-size: 0.8125rem;
        font-weight: 600;
        color: #000000;
        background: transparent;
      }
      .quantity-selector input::-webkit-outer-spin-button,
      .quantity-selector input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      .cart-items__remove {
        background: transparent;
        border: none;
        cursor: pointer;
        color: #6b7280;
        padding: 0.25rem;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: color 0.2s ease;
      }
      .cart-items__remove:hover {
        color: #dc2626;
      }
      
      .cart-drawer__summary {
        scrollbar-color: rgb(0 0 0 / 0.4) #ffffff;
        --hover-shadow-color: rgba(0,0,0,0.1);
        --hover-transition-duration: .25s;
        --hover-transition-timing: ease-out;
        --theme-drawer-width: 30rem;
        --font-body--family: Poppins, sans-serif;
        --icon-stroke-width: 1.5px;
        --style-border-radius-inputs: 4px;
        --style-border-radius-buttons-primary: 8px;
        --padding-xl: 0.85rem;
        --padding-2xl: 1rem;
        --gap-xl: 0.55rem;
        --color-background: #ffffff;
        --color-foreground: #000000;
        --color-border: rgba(0, 0, 0, 0.07);
        --cart-drawer-summary-padding: 0.95rem 1.35rem 1.1rem 1.35rem;

        box-sizing: border-box !important;
        position: sticky !important;
        bottom: 0 !important;
        z-index: 10 !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: stretch !important;
        gap: 0.55rem !important;
        padding: 0.95rem 1.35rem 1.1rem 1.35rem !important;
        margin-top: auto !important;
        background: rgba(255, 255, 255, 0.88) !important;
        backdrop-filter: blur(16px) !important;
        -webkit-backdrop-filter: blur(16px) !important;
        border-top: 1px solid rgba(0, 0, 0, 0.07) !important;
        box-shadow: 0 -4px 16px rgba(0,0,0,0.04) !important;
      }

      @media (max-width: 767px) {
        .cart-drawer__summary {
          padding: 0.85rem 1.15rem 1rem 1.15rem !important;
          gap: 0.5rem !important;
        }
      }

      .cart-totals {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
        width: 100%;
        line-height: 1.35;
      }

      .cart-totals__original-container {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .cart-totals__original {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 0.85rem;
        font-weight: 500;
        color: #000000;
        line-height: 1.3;
      }

      .cart-totals__original-label {
        font-weight: 500;
      }

      .cart-totals__original-value {
        font-weight: 600;
      }

      .cart-discounts {
        list-style: none;
        padding: 0;
        margin: 0.1rem 0 0 0;
      }

      .cart-discounts__item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 0.75rem;
        font-weight: 500;
        color: #000000;
      }

      .cart-discounts__label {
        display: flex;
        align-items: center;
        gap: 0.3rem;
        color: #000000;
      }

      .cart-discounts__label svg {
        flex-shrink: 0;
      }

      .cart-discounts__value {
        font-weight: 500;
        color: #000000;
      }

      .cart-actions {
        width: 100%;
      }

      accordion-custom.cart-discount {
        display: block;
        border-top: 1px solid rgba(0, 0, 0, 0.05);
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        padding: 0.25rem 0;
        margin: 0;
      }

      .cart-discount details {
        width: 100%;
      }

      .cart-discount__summary {
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;
        list-style: none;
        user-select: none;
        padding: 2px 0;
      }

      .cart-discount__summary::-webkit-details-marker {
        display: none;
      }

      .cart-discount__label {
        font-size: 0.8125rem;
        font-weight: 500;
        color: #000000;
      }

      .cart-totals__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        color: #000000;
        width: 13px;
        height: 13px;
      }

      .cart-totals__icon svg {
        display: block;
        width: 13px;
        height: 13px;
      }

      .cart-totals__icon svg .minus-bar {
        stroke: currentColor;
        stroke-width: 1.5;
        stroke-linecap: round;
      }

      .cart-totals__icon svg .vertical-bar {
        stroke: currentColor;
        stroke-width: 1.5;
        stroke-linecap: round;
        transform-origin: 6.5px 6.5px;
        transition: transform 0.32s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease !important;
      }

      /* When open: vertical line collapses cleanly leaving a pure, proper minus icon */
      details[open] .cart-totals__icon svg .vertical-bar,
      details.is-open .cart-totals__icon svg .vertical-bar {
        transform: scaleY(0) !important;
        opacity: 0 !important;
      }

      details:not([open]) .cart-totals__icon svg .vertical-bar {
        transform: scaleY(1) !important;
        opacity: 1 !important;
      }

      .details-content {
        overflow: hidden !important;
        padding-top: 0;
        will-change: height, opacity;
      }

      .cart-discount__form {
        display: flex;
        gap: 0.4rem;
        align-items: center;
        padding-top: 0.25rem;
      }

      .cart-discount__input {
        flex: 1;
        height: 2.1rem;
        padding: 0 0.65rem;
        border: 1px solid #DFDFDF;
        border-radius: 4px;
        font-family: Poppins, sans-serif;
        font-size: 0.75rem;
        color: #000000;
        background: #ffffff;
        outline: none;
        transition: border-color 0.2s;
        text-transform: uppercase;
      }

      .cart-discount__input:focus {
        border-color: #000000;
      }

      .cart-discount__button {
        height: 2.1rem;
        padding: 0 0.95rem;
        background: #000000;
        color: #ffffff;
        border: none;
        border-radius: 4px;
        font-family: Poppins, sans-serif;
        font-size: 0.75rem;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s ease;
      }

      .cart-discount__button:hover {
        background: #262626;
      }

      .cart-discount__error {
        margin-top: 0.35rem;
        display: flex;
        align-items: center;
        gap: 0.35rem;
        color: #8B0000;
        font-size: 0.7rem;
      }

      .cart-discount__error.hidden {
        display: none !important;
      }

      .cart-totals__container {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
        margin-top: 0.15rem;
      }

      .cart-totals__total {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        line-height: 1.3;
      }

      .cart-totals__total-label {
        font-size: 0.9rem;
        font-weight: 600;
        color: #000000;
      }

      .cart-totals__total-value {
        font-size: 1rem;
        font-weight: 700;
        color: #000000;
      }

      .cart-totals__tax-note {
        font-size: 0.7rem;
        color: #6b7280;
        line-height: 1.25;
        margin-top: 1px;
      }

      .cart-totals__tax-note a {
        color: inherit;
        text-decoration: underline;
      }

      .cart__ctas {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 100%;
        margin-top: 0.25rem;
      }

      .es-popup-button-drawer,
      .cart__checkout-button {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        height: 2.65rem !important;
        background: #000000 !important;
        color: #FFFFFF !important;
        border-radius: 8px !important;
        font-size: 0.85rem !important;
        font-weight: 600 !important;
        cursor: pointer !important;
        border: none !important;
        width: 100% !important;
        transition: background-color 0.2s ease, transform 0.15s ease !important;
      }

      .es-popup-button-drawer:hover,
      .cart__checkout-button:hover {
        background: #1f2937 !important;
      }

      .es-popup-button-drawer:active,
      .cart__checkout-button:active {
        transform: scale(0.98) !important;
      }
      #theme-drawer-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.52);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.42s cubic-bezier(0.16, 1, 0.3, 1), backdrop-filter 0.42s cubic-bezier(0.16, 1, 0.3, 1);
        z-index: 9999;
        will-change: opacity, backdrop-filter;
      }
      #theme-drawer-backdrop.active {
        opacity: 1;
        pointer-events: auto;
      }
      @media (max-width: 768px) {
        .thudarum-toast-container,
        [data-sonner-toaster],
        [data-radix-toast-announce-exclude] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      }
    `;
    document.head.appendChild(styleTag);

    root = document.createElement('div');
    root.id = 'stylux-side-cart-root';
    root.className = 'fixed inset-0 pointer-events-none';
    root.style.zIndex = '9999';
    root.innerHTML = `
      <!-- Backdrop Dim Overlay -->
      <div id="theme-drawer-backdrop"></div>

      <!-- Dialog Panel matching OuterHTML -->
      <dialog ref="panel" class="theme-drawer__dialog color-custom-drawer" aria-labelledby="cart-drawer-heading" cart-summary-sticky="true">
        <div class="theme-drawer__grab-handle-bar">
          <span class="theme-drawer__grab-handle-pill"></span>
        </div>
        <cart-drawer-component auto-open="" style="display: flex; flex-direction: column; height: 100%; width: 100%; min-height: 0;">
          
          <div class="cart-drawer__inner" style="display: flex; flex-direction: column; height: 100%; width: 100%;">
            
            <!-- Header -->
            <div class="theme-drawer__header">
              <h2 class="theme-drawer__title h4" id="cart-drawer-heading">
                Cart
                <span class="theme-drawer__badge" id="theme-drawer-badge" aria-hidden="true">0</span>
              </h2>

              <button class="theme-drawer__close-button" id="theme-drawer-close" type="button" aria-label="Close">
                <span class="svg-wrapper">
                  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.1765 12.025C11.4108 12.2594 11.7907 12.2594 12.025 12.025C12.2594 11.7907 12.2594 11.4108 12.025 11.1765L8.84931 8.00078L12.025 4.82505C12.2594 4.59073 12.2594 4.21083 12.025 3.97652C11.7907 3.7422 11.4108 3.7422 11.1765 3.97652L8.00078 7.15225L4.82505 3.97652C4.59073 3.7422 4.21083 3.7422 3.97652 3.97652C3.7422 4.21083 3.7422 4.59073 3.97652 4.82505L7.15225 8.00078L3.97652 11.1765C3.7422 11.4108 3.7422 11.7907 3.97652 12.025C4.21083 12.2594 4.59073 12.2594 4.82505 12.025L8.00078 8.84931L11.1765 12.025Z" fill="currentColor"></path>
                  </svg>
                </span>
              </button>
            </div>

            <!-- Scrollable Items List -->
            <div class="cart-drawer__content" style="flex: 1; overflow-y: auto; padding: 0 1.5rem;" aria-label="Cart">
              <div class="cart-items__wrapper" id="cart-items-wrapper">
                <form action="/cart" class="cart-form" id="cart-form" method="post">
                  <div class="cart-items">
                    <table class="cart-items__table" role="table">
                      <tbody role="rowgroup" id="cart-items-table-body">
                        <!-- Dynamic Rows Injected Here -->
                      </tbody>
                    </table>
                  </div>
                </form>
              </div>
            </div>

            
            <!-- Footer / Summary matching exact OuterHTML -->
            <div class="cart-drawer__summary" id="cart-drawer-summary" style="display: none;">
              <div class="cart-totals">
                
                <div class="cart-totals__original-container cart-primary-typography">
                  <span class="cart-totals__item cart-totals__original">
                    <span class="cart-totals__original-label">Subtotal</span>
                    <span class="cart-totals__original-value cart-secondary-typography" id="cart-subtotal-value" data-testid="cart-subtotal-value">Rs.0.00</span>
                  </span>
                  <ul class="cart-discounts" id="cart-discounts-list" role="list" aria-label="Discounts" style="display: none;">
                    <li class="cart-discounts__item">
                      <span class="cart-discounts__label">
                        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M13.25 4.25H11.2759C10.4606 4.25 9.68048 4.58181 9.11496 5.16908L4.86136 9.58629C4.10538 10.3713 4.11713 11.6171 4.88778 12.3878L7.78569 15.2857C8.46001 15.96 9.55008 15.9703 10.237 15.3088L14.9841 10.7375C15.4735 10.2663 15.75 9.61613 15.75 8.93673V6.75C15.75 5.36929 14.6307 4.25 13.25 4.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                          <circle cx="13" cy="7" r="1" fill="currentColor"></circle>
                        </svg>
                        Buy 2 &amp; Save Rs. 500 
                      </span>
                      <span class="cart-discounts__value cart-secondary-typography" id="cart-discount-value">-Rs.0.00</span>
                    </li>
                  </ul>
                </div>

                <div class="cart-actions">
                  <accordion-custom class="cart-discount">
                    <details id="discount-details">
                      <summary class="cart-discount__summary">
                        <span class="cart-discount__label cart-primary-typography">Discount</span>
                        <span class="cart-totals__icon" aria-hidden="true">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path class="minus-bar" d="M2.5 7H11.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
                            <path class="vertical-bar" d="M7 2.5V11.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
                          </svg>
                        </span>
                      </summary>

                      <div class="details-content">
                        <div id="cart-discount-disclosure">
                          <cart-discount-component data-section-id="cart-drawer-section">
                            <div class="cart-discount__content">
                              <form id="drawer-discount-form" onsubmit="return false;" class="cart-discount__form">
                                <label for="cart-discount" class="visually-hidden" style="position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);">Apply a discount code</label>
                                <input id="drawer-discount-input" class="cart-discount__input" type="text" name="discount" size="8" placeholder="Discount code" required="" data-skip-node-update="">
                                <button type="submit" id="drawer-discount-submit" class="button cart-discount__button">
                                  Apply
                                </button>
                              </form>
                            </div>
                            <div id="drawer-discount-error" class="cart-discount__error hidden" role="alert" ref="cartDiscountError">
                              <span class="svg-wrapper">
                                <svg aria-hidden="true" class="icon icon-error" width="13" height="13" viewBox="0 0 13 13">
                                  <circle cx="6.5" cy="6.5" r="5.5" stroke="#fff" stroke-width="1.5"></circle>
                                  <circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width=".7"></circle>
                                  <path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"></path>
                                  <path fill="#fff" stroke="#EB001B" stroke-width=".7" d="M5.874 3.178h-.359l.01.359.1 4.044.008.341h1.736l.008-.341.1-4.044.01-.359H5.873Zm.627 6.833c.56 0 1.03-.432 1.03-1.006s-.47-1.006-1.03-1.006-1.031.432-1.031 1.006.47 1.006 1.03 1.006Z"></path>
                                </svg>
                              </span>
                              <small class="cart-discount__error-text cart-primary-typography" id="drawer-discount-error-text">
                                Discount code cannot be applied to your cart
                              </small>
                            </div>
                            <ul class="cart-discount__codes" id="drawer-applied-codes"></ul>
                          </cart-discount-component>
                        </div>
                      </div>
                    </details>
                  </accordion-custom>
                </div>

                <div class="cart-totals__container">
                  <span class="cart-totals__item cart-totals__total" role="status">
                    <span class="cart-totals__total-label cart-primary-typography">Estimated total</span>
                    <text-component id="cart-total-value" class="cart-totals__total-value cart-secondary-typography" data-testid="cart-total-value" data-cart-subtotal="">
                      Rs.0.00 PKR
                    </text-component>
                  </span>
                  <div class="cart-totals__item cart-totals__tax-note cart-primary-typography">
                    <small>Taxes and <a href="/policies/shipping-policy">shipping</a> calculated at checkout.</small>
                  </div>
                </div>

              </div>

              <!-- Action Buttons -->
              <div class="cart__ctas">
                <div id="es-popup-button" class="es-popup-button es-popup-button-drawer" style="color: #FFFFFF !important;font-style: normal !important;background: #000000 !important;font-weight: 500 !important;border-color: #ffffff !important;border-style: solid !important;border-width: 0px !important;border-radius: 6px !important;box-shadow-opacity: 0px !important;">
                  <div style="margin:0 10px;"><i class="bi bi-bag"></i></div> 
                  <div><span style="color: #FFFFFF;" class="es-button-title">Buy With Cash on Delivery</span></div>
                </div>
              </div>

            </div>

          </div>

        </cart-drawer-component>
      </dialog>
    `;

    document.body.appendChild(root);

    // Event listeners
    const backdrop = root.querySelector('#theme-drawer-backdrop');
    const closeBtn = root.querySelector('#theme-drawer-close');
    const dialogEl = root.querySelector('dialog.theme-drawer__dialog');

    if (backdrop) {
      backdrop.onclick = (e) => {
        if (e.target === backdrop) {
          closeSideCart();
        }
      };
    }
    if (closeBtn) {
      const handleClose = (e) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        closeSideCart();
      };
      closeBtn.onclick = handleClose;
      closeBtn.addEventListener('click', handleClose);
      closeBtn.addEventListener('touchend', handleClose);
    }

    // Stop all clicks inside the drawer panel from bubbling to backdrop
    if (dialogEl) {
      dialogEl.addEventListener('click', (e) => {
        e.stopPropagation();
      });
      dialogEl.addEventListener('mousedown', (e) => {
        e.stopPropagation();
      });

      // Continuous Pointer Drag Resizing for Mobile Cart Bottom Sheet
      const handleBar = dialogEl.querySelector('.theme-drawer__grab-handle-bar');
      const headerBar = dialogEl.querySelector('.theme-drawer__header');
      const dragHandles = [handleBar, headerBar].filter(Boolean);

      dragHandles.forEach(handle => {
        handle.style.touchAction = 'none';
        let isDragging = false;
        let startY = 0;
        let startHeight = 0;
        let currentHeight = 0;
        let startTime = 0;
        let pointerId = null;

        const onPointerDown = (e) => {
          if (window.innerWidth >= 768) return;
          if (e.button && e.button !== 0) return;
          if (e.target.closest('#theme-drawer-close')) return;

          isDragging = true;
          pointerId = e.pointerId;
          startY = e.clientY;
          startTime = Date.now();

          const rect = dialogEl.getBoundingClientRect();
          startHeight = rect.height;
          currentHeight = startHeight;

          try {
            handle.setPointerCapture(pointerId);
          } catch (_) {}

          dialogEl.classList.add('is-dragging');
          dialogEl.style.setProperty('transition', 'none', 'important');
          dialogEl.style.setProperty('will-change', 'height, max-height', 'important');
          document.body.style.userSelect = 'none';
          document.body.style.webkitUserSelect = 'none';
        };

        const onPointerMove = (e) => {
          if (!isDragging || (pointerId !== null && e.pointerId !== pointerId)) return;

          const deltaY = e.clientY - startY;
          const vh = window.innerHeight || document.documentElement.clientHeight;
          const minHeight = Math.max(180, vh * 0.28);
          const maxHeight = Math.min(vh * 0.92, vh - 32);

          // deltaY > 0 means dragging downward -> reduces height
          // deltaY < 0 means dragging upward -> increases height
          let targetHeight = startHeight - deltaY;
          if (targetHeight > maxHeight) {
            targetHeight = maxHeight + (targetHeight - maxHeight) * 0.18;
          }
          currentHeight = targetHeight;

          dialogEl.style.setProperty('height', `${Math.round(targetHeight)}px`, 'important');
          dialogEl.style.setProperty('max-height', `${Math.round(targetHeight)}px`, 'important');
          dialogEl.style.transform = 'translate3d(0, 0, 0)';
        };

        const onPointerUp = (e) => {
          if (!isDragging || (pointerId !== null && e.pointerId !== pointerId)) return;
          isDragging = false;

          try {
            handle.releasePointerCapture(pointerId);
          } catch (_) {}
          pointerId = null;

          dialogEl.classList.remove('is-dragging');
          document.body.style.userSelect = '';
          document.body.style.webkitUserSelect = '';

          const deltaY = e.clientY - startY;
          const elapsed = Math.max(Date.now() - startTime, 1);
          const velocityY = deltaY / elapsed;
          const vh = window.innerHeight || document.documentElement.clientHeight;
          const minHeight = Math.max(180, vh * 0.28);
          const maxHeight = Math.min(vh * 0.92, vh - 32);
          const closeThreshold = vh * 0.18;

          dialogEl.style.setProperty('transition', 'height 0.28s cubic-bezier(0.16, 1, 0.3, 1), max-height 0.28s cubic-bezier(0.16, 1, 0.3, 1)', 'important');

          // Fast downward flick or dragged below close threshold -> Close side cart
          if ((velocityY > 0.65 && deltaY > 60) || currentHeight < closeThreshold) {
            closeSideCart();
            return;
          }

          // Snap cleanly to max constraint if dragged over
          if (currentHeight > maxHeight) {
            dialogEl.style.setProperty('height', `${Math.round(maxHeight)}px`, 'important');
            dialogEl.style.setProperty('max-height', `${Math.round(maxHeight)}px`, 'important');
            dialogEl.classList.add('is-expanded');
            return;
          }

          // Snap cleanly to min constraint if dragged below min
          if (currentHeight < minHeight) {
            dialogEl.style.setProperty('height', `${Math.round(minHeight)}px`, 'important');
            dialogEl.style.setProperty('max-height', `${Math.round(minHeight)}px`, 'important');
            dialogEl.classList.remove('is-expanded');
            return;
          }

          // Leave at exact custom dragged intermediate height
          dialogEl.style.setProperty('height', `${Math.round(currentHeight)}px`, 'important');
          dialogEl.style.setProperty('max-height', `${Math.round(currentHeight)}px`, 'important');
          if (currentHeight > vh * 0.75) {
            dialogEl.classList.add('is-expanded');
          } else {
            dialogEl.classList.remove('is-expanded');
          }
        };

        handle.addEventListener('pointerdown', onPointerDown);
        handle.addEventListener('pointermove', onPointerMove);
        handle.addEventListener('pointerup', onPointerUp);
        handle.addEventListener('pointercancel', onPointerUp);
      });
    }

    // Smooth Luxury Slide Accordion for Discount (non-linear cubic-bezier curve)
    const discountDetails = root.querySelector('#discount-details');
    const discountSummary = root.querySelector('.cart-discount__summary');
    const discountContent = root.querySelector('.details-content');

    if (discountSummary && discountDetails && discountContent) {
      let isDiscountAnimating = false;

      discountSummary.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isDiscountAnimating) return;
        isDiscountAnimating = true;

        const isOpen = discountDetails.hasAttribute('open');

        if (isOpen) {
          // Slide up (closing)
          const startHeight = discountContent.scrollHeight;
          discountContent.style.height = startHeight + 'px';
          discountContent.style.opacity = '1';
          discountContent.offsetHeight; // force reflow

          discountContent.style.transition = 'height 0.38s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1)';
          discountContent.style.height = '0px';
          discountContent.style.opacity = '0';
          discountDetails.classList.remove('is-open');

          const onTransitionEnd = () => {
            discountDetails.removeAttribute('open');
            discountContent.style.height = '';
            discountContent.style.opacity = '';
            discountContent.style.transition = '';
            isDiscountAnimating = false;
            discountContent.removeEventListener('transitionend', onTransitionEnd);
          };
          discountContent.addEventListener('transitionend', onTransitionEnd);
        } else {
          // Slide down (opening)
          discountDetails.setAttribute('open', '');
          discountDetails.classList.add('is-open');
          discountContent.style.height = '0px';
          discountContent.style.opacity = '0';
          discountContent.offsetHeight; // force reflow

          const targetHeight = discountContent.scrollHeight;
          discountContent.style.transition = 'height 0.38s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1)';
          discountContent.style.height = targetHeight + 'px';
          discountContent.style.opacity = '1';

          const onTransitionEnd = () => {
            discountContent.style.height = '';
            discountContent.style.opacity = '';
            discountContent.style.transition = '';
            isDiscountAnimating = false;
            discountContent.removeEventListener('transitionend', onTransitionEnd);
          };
          discountContent.addEventListener('transitionend', onTransitionEnd);
        }
      });
    }

    // Promo Code submit & real Shopify discount validation
    const discountForm = root.querySelector('#drawer-discount-form');
    const discountErr = root.querySelector('#drawer-discount-error');
    if (discountForm) {
      discountForm.onsubmit = async (e) => {
        e.preventDefault();
        const input = root.querySelector('#drawer-discount-input');
        const submitBtn = root.querySelector('#drawer-discount-submit');
        const val = (input?.value || '').trim().toUpperCase();
        if (!val) return;
        
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = '...';
        }

        try {
          const currentCart = getCart();
          const validation = await validateShopifyDiscount(val, currentCart);

          if (validation && validation.valid) {
            // Send live discount activation to Shopify session
            fetch('/discount/' + encodeURIComponent(val), { method: 'GET', credentials: 'same-origin' }).catch(() => {});

            if (discountErr) discountErr.classList.add('hidden');

            saveCoupon({
              code: validation.code || val,
              type: validation.type || 'flat',
              value: typeof validation.value === 'number' ? validation.value : 0,
              description: validation.description || `Shopify Promo Code (${val})`
            });

            if (input) input.value = '';
            showToast('Promo Code Applied', `Code "${val}" is active and applied!`);
          } else {
            // Code is invalid or inactive
            if (discountErr) {
              discountErr.classList.remove('hidden');
              const errText = discountErr.querySelector('#drawer-discount-error-text');
              if (errText) errText.textContent = validation?.error || `Promo code "${val}" is invalid or inactive`;
            }
            showToast('Invalid Code', validation?.error || `Promo code "${val}" is not valid`, 'error');
          }
        } catch(err) {
          if (discountErr) {
            discountErr.classList.remove('hidden');
            const errText = discountErr.querySelector('#drawer-discount-error-text');
            if (errText) errText.textContent = `Promo code "${val}" could not be verified`;
          }
        } finally {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Apply';
          }
        }
      };
    }

    // Cash on delivery - Direct Checkout Navigation Integration
    const codBtn = root.querySelector('#es-popup-button');
    if (codBtn) {
      codBtn.onclick = (e) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        proceedToShopifyCheckout(codBtn);
      };
    }

    return root;
  }

  function openSideCart() {
    const root = ensureSideCartDom();
    const backdrop = root.querySelector('#theme-drawer-backdrop');
    const dialog = root.querySelector('dialog.theme-drawer__dialog');

    renderSideCartContent();

    root.classList.remove('pointer-events-none');
    dialog.setAttribute('open', '');
    // Force reflow to guarantee CSS transition initiates smoothly
    dialog.offsetHeight;

    dialog.classList.add('is-expanded');
    if (window.innerWidth < 768) {
      dialog.style.setProperty('height', '88dvh', 'important');
      dialog.style.setProperty('max-height', '88dvh', 'important');
    }

    requestAnimationFrame(() => {
      backdrop.classList.add('active');
      dialog.classList.add('active');
    });

    document.body.style.overflow = 'hidden';
    document.body.classList.add('cart-drawer-open');
    // Hide floating Filter/Brand button when Bag/Cart opens
    const floatingPills = document.querySelectorAll('.mpm_pill_dock');
    floatingPills.forEach(p => {
      p.classList.add('is-hidden');
      p.style.setProperty('display', 'none', 'important');
      p.style.setProperty('opacity', '0', 'important');
      p.style.setProperty('pointer-events', 'none', 'important');
    });
    isSideCartOpen = true;
  }

  let isSideCartClosing = false;
  function closeSideCart() {
    const root = document.getElementById('stylux-side-cart-root');
    if (!root) {
      document.body.style.overflow = '';
      isSideCartOpen = false;
      isSideCartClosing = false;
      return;
    }
    const backdrop = root.querySelector('#theme-drawer-backdrop');
    const dialog = root.querySelector('dialog.theme-drawer__dialog');

    if (backdrop) backdrop.classList.remove('active');
    if (dialog) dialog.classList.remove('active');

    isSideCartClosing = true;

    // Wait for the smooth non-linear slide down transition (360ms) to complete before removing open attribute
    setTimeout(() => {
      if (dialog) {
        dialog.removeAttribute('open');
        dialog.style.removeProperty('height');
        dialog.style.removeProperty('max-height');
        dialog.style.transform = '';
      }
      root.classList.add('pointer-events-none');
      document.body.style.overflow = '';
      document.body.classList.remove('cart-drawer-open');
      const floatingPills = document.querySelectorAll('.mpm_pill_dock');
      floatingPills.forEach(p => {
        p.classList.remove('is-hidden');
        p.style.removeProperty('display');
        p.style.removeProperty('opacity');
        p.style.removeProperty('pointer-events');
      });
      isSideCartOpen = false;
      isSideCartClosing = false;
    }, 360);
  }

  function renderSideCartContent() {
    const root = document.getElementById('stylux-side-cart-root');
    if (!root) return;

    const tbody = root.querySelector('#cart-items-table-body');
    const badge = root.querySelector('#theme-drawer-badge');
    const subtotalEl = root.querySelector('#cart-subtotal-value');
    const discountList = root.querySelector('#cart-discounts-list');
    const discountValEl = root.querySelector('#cart-discount-value');
    const totalValEl = root.querySelector('#cart-total-value');
    const summary = root.querySelector('#cart-drawer-summary');

    const cart = getCart();
    const totalCount = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);

    if (badge) badge.textContent = totalCount;
    document.querySelectorAll('[data-cart-count], .cart-count-badge').forEach(el => { el.textContent = totalCount; });

    if (cart.length === 0) {
      if (subtotalEl) subtotalEl.textContent = formatRs(0);
      if (discountValEl) discountValEl.textContent = '-Rs.0.00';
      if (discountList) discountList.style.display = 'none';
      if (totalValEl) totalValEl.textContent = formatRs(0) + ' PKR';
      if (summary) summary.style.display = 'none';

      tbody.innerHTML = `
        <tr class="cart-items__table-row" style="border:none; padding: 3rem 0; text-align: center; display: block;">
          <td colspan="4" style="display: block;">
            <h2 style="font-size: 1.25rem; font-weight: 500; margin-bottom: 0.5rem;">Your cart is empty</h2>
            <p style="font-size: 0.875rem; color: #6b7280; margin-bottom: 1.5rem;">Have an account? <a href="/account/login" style="text-decoration: underline;">Log in</a> to check out faster.</p>
            <a href="/collections/all" class="button" style="display: inline-flex; align-items: center; justify-content: center; background: #000; color: #fff; padding: 0.75rem 1.5rem; border-radius: 4px; font-size: 0.875rem; font-weight: 500; text-decoration: none;">
              Continue shopping
            </a>
          </td>
        </tr>
      `;
      return;
    }

    if (summary) summary.style.display = 'block';

    const rawSubtotal = cart.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
    
    // Calculate discounts
    const coupon = getCoupon();
    let promoDiscountAmount = 0;
    let promoLabel = 'Promo Discount';

    if (coupon) {
      if (coupon.type === 'flat' || (coupon.discount && coupon.discount > 100)) {
        promoDiscountAmount = coupon.value || coupon.discount || 0;
        promoLabel = `Promo Code (${coupon.code}) - Flat Rs.${promoDiscountAmount}`;
      } else {
        const pct = coupon.value || coupon.discount || 0;
        promoDiscountAmount = Math.round((rawSubtotal * pct) / 100);
        promoLabel = `Promo Code (${coupon.code}) - ${pct}% Off`;
      }
    }

    let bundleDiscountAmount = 0;
    if (totalCount >= 2) {
      bundleDiscountAmount = 500;
    }

    const totalDiscountAmount = promoDiscountAmount + bundleDiscountAmount;
    const finalTotal = Math.max(0, rawSubtotal - totalDiscountAmount);

    if (subtotalEl) subtotalEl.textContent = formatRs(rawSubtotal);

    if (totalDiscountAmount > 0) {
      if (discountList) {
        discountList.style.display = 'block';
        discountList.innerHTML = `
          ${bundleDiscountAmount > 0 ? `
            <li class="cart-discounts__item" style="display:flex; justify-content:space-between; align-items:center; margin-bottom: 4px;">
              <span class="cart-discounts__label" style="display:flex; align-items:center; gap:6px; color:#16a34a; font-weight:500; font-size:0.8125rem;">
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.25 4.25H11.2759C10.4606 4.25 9.68048 4.58181 9.11496 5.16908L4.86136 9.58629C4.10538 10.3713 4.11713 11.6171 4.88778 12.3878L7.78569 15.2857C8.46001 15.96 9.55008 15.9703 10.237 15.3088L14.9841 10.7375C15.4735 10.2663 15.75 9.61613 15.75 8.93673V6.75C15.75 5.36929 14.6307 4.25 13.25 4.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                  <circle cx="13" cy="7" r="1" fill="currentColor"></circle>
                </svg>
                Buy 2 &amp; Save Rs. 500
              </span>
              <span class="cart-discounts__value cart-secondary-typography" style="color:#16a34a; font-weight:600; font-size:0.8125rem;">-Rs.500.00</span>
            </li>
          ` : ''}
          ${promoDiscountAmount > 0 ? `
            <li class="cart-discounts__item" style="display:flex; justify-content:space-between; align-items:center;">
              <span class="cart-discounts__label" style="display:flex; align-items:center; gap:6px; color:#16a34a; font-weight:500; font-size:0.8125rem;">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>
                ${promoLabel}
              </span>
              <span class="cart-discounts__value cart-secondary-typography" style="color:#16a34a; font-weight:600; font-size:0.8125rem;">-${formatRs(promoDiscountAmount)}</span>
            </li>
          ` : ''}
        `;
      }
    } else {
      if (discountList) discountList.style.display = 'none';
    }

    // Render applied coupon tag/badge inside the accordion
    const appliedCodesList = root.querySelector('#drawer-applied-codes');
    if (appliedCodesList) {
      if (coupon) {
        let tagText = '(Applied)';
        if (coupon.type === 'flat' && coupon.value > 0) {
          tagText = `(Rs.${coupon.value} OFF)`;
        } else if (coupon.type === 'percentage' && coupon.value > 0) {
          tagText = `(${coupon.value}% OFF)`;
        }

        appliedCodesList.innerHTML = `
          <li style="display:inline-flex; align-items:center; gap:8px; margin-top:8px; padding:5px 10px; background:#ecfdf5; border:1px solid #a7f3d0; border-radius:6px; font-size:0.75rem; font-weight:600; color:#047857;">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>
            <span>${coupon.code}</span>
            <span style="color:#047857; font-size:11px; font-weight:500;">${tagText}</span>
            <button type="button" id="drawer-remove-coupon-btn" style="background:none; border:none; color:#047857; cursor:pointer; padding:0; margin-left:4px; font-size:13px; line-height:1; display:flex; align-items:center;" title="Remove promo code">✕</button>
          </li>
        `;
        const removeBtn = appliedCodesList.querySelector('#drawer-remove-coupon-btn');
        if (removeBtn) {
          removeBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            saveCoupon(null);
            showToast('Promo code removed', 'Code was removed from cart');
          };
        }
      } else {
        appliedCodesList.innerHTML = '';
      }
    }

    if (totalValEl) totalValEl.textContent = formatRs(finalTotal) + ' PKR';

    // Render Table Rows matching OuterHTML
    tbody.innerHTML = cart.map(item => `
      <tr class="cart-items__table-row cart-items__table-row--full-width-variants" data-key="${item.id}">
        <!-- Product Image -->
        <td class="cart-items__media" role="cell">
          <a href="/products/${item.id}" class="cart-items__media-container" style="display:block; width:100%; height:100%;">
            <img src="${item.image}" onerror="this.src='${item.fallbackImage || item.image}'" alt="${item.name}" class="cart-items__media-image" loading="lazy">
          </a>
        </td>

        <!-- Product Information & Details -->
        <td class="cart-items__details" role="cell">
          <div class="cart-items__product-info">
            <div class="cart-items__title-row">
              <a href="/products/${item.id}" class="cart-items__title">${item.name}</a>
            </div>
          </div>

          <div class="cart-items__variants-wrapper">
            <span class="cart-items__variant">${item.variant || 'Silver'}</span>
          </div>

          <div class="cart-items__unit-price-wrapper">
            <span>${formatRs(item.price)}</span>
            <s class="compare-at-price">${formatRs(item.originalPrice || item.price * 1.6)}</s>
          </div>

          <div class="cart-items__quantity-controls">
            <!-- Quantity Selector Component -->
            <div class="quantity-selector-wrapper">
              <div class="quantity-selector" data-variant-id="${item.id}">
                <button class="button quantity-minus" type="button" data-id="${item.id}">
                  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.75 7H11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
                  </svg>
                </button>
                <input type="number" value="${item.quantity || 1}" readonly>
                <button class="button quantity-plus" type="button" data-id="${item.id}">
                  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path class="vertical" d="M2.75 7H11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
                    <path class="horizontal" d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Remove Button -->
            <button class="button cart-items__remove" type="button" aria-label="Remove ${item.name}" data-id="${item.id}">
              <svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
                <path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </button>
          </div>
        </td>
      </tr>
    `).join('');

    // Event listeners for quantity & removal with event stopPropagation
    tbody.querySelectorAll('.quantity-minus').forEach(btn => {
      btn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        updateItemQuantity(btn.dataset.id, -1);
      };
    });

    tbody.querySelectorAll('.quantity-plus').forEach(btn => {
      btn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        updateItemQuantity(btn.dataset.id, 1);
      };
    });

    tbody.querySelectorAll('.cart-items__remove').forEach(btn => {
      btn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        removeFromCart(btn.dataset.id);
      };
    });

    // Also update main cart page if present
    renderMainCartPage();
  }

  // --- 5B. MAIN CART & CHECKOUT FLOW PAGE HYDRATION ---
  function renderMainCartPage() {
    const root = document.getElementById('thudarum-cart-root');
    if (!root) return;

    const cart = getCart();
    const totalCount = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);

    if (cart.length === 0) {
      root.innerHTML = `
        <div class="container mx-auto px-4 sm:px-6 py-6 sm:py-8 md:py-12">
          <div class="max-w-2xl mx-auto text-center py-12 sm:py-16">
            <a data-slot="button" class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2 mb-6" href="/collections/all">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-left h-4 w-4 mr-2"><path d="m12 19-7-7 7-7"></path><path d="M19 12H5"></path></svg>
              Continue Shopping
            </a>
            <h1 class="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold mb-4">Shopping Cart</h1>
            <p class="text-muted-foreground mb-8">Your shopping cart is empty</p>
            <a data-slot="button" class="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-6 h-11 text-sm sm:text-base" href="/collections/all">
              Explore Collection
            </a>
          </div>
        </div>
      `;
      return;
    }

    const rawSubtotal = cart.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
    const coupon = getCoupon();
    let promoDiscountAmount = 0;
    let promoLabel = 'Promo Discount';

    if (coupon) {
      if (coupon.type === 'flat' || (coupon.discount && coupon.discount > 100)) {
        promoDiscountAmount = coupon.value || coupon.discount || 0;
        promoLabel = `Promo Code (${coupon.code}) - Flat Rs.${promoDiscountAmount}`;
      } else {
        const pct = coupon.value || coupon.discount || 0;
        promoDiscountAmount = Math.round((rawSubtotal * pct) / 100);
        promoLabel = `Promo Code (${coupon.code}) - ${pct}% Off`;
      }
    }

    let bundleDiscountAmount = 0;
    if (totalCount >= 2) bundleDiscountAmount = 500;
    const totalDiscountAmount = promoDiscountAmount + bundleDiscountAmount;
    const finalTotal = Math.max(0, rawSubtotal - totalDiscountAmount);

    root.innerHTML = `
      <div class="container mx-auto px-4 sm:px-6 py-8 md:py-12" style="max-width: 1200px;">
        <div style="margin-bottom: 2rem;">
          <a data-slot="button" class="inline-flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-black mb-4" href="/collections/all">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 19-7-7 7-7"></path><path d="M19 12H5"></path></svg>
            Continue Shopping
          </a>
          <h1 class="font-serif text-3xl sm:text-4xl font-semibold">Checkout &amp; Shopping Bag</h1>
          <p class="text-sm text-muted-foreground mt-1">${totalCount} timepiece${totalCount > 1 ? 's' : ''} in your cart</p>
        </div>

        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 2.5rem; align-items: start;">
          <!-- Left: Cart Items List -->
          <div style="background: #ffffff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 1.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
            <h2 style="font-size: 1.125rem; font-weight: 600; margin-bottom: 1.25rem; border-bottom: 1px solid #f3f4f6; padding-bottom: 0.75rem;">Selected Timepieces</h2>
            <div style="display: flex; flex-direction: column; gap: 1.25rem;">
              ${cart.map(item => `
                <div style="display: flex; gap: 1rem; align-items: center; border-bottom: 1px solid #f3f4f6; padding-bottom: 1.25rem;">
                  <img src="${item.image}" alt="${item.name}" style="width: 72px; height: 72px; object-fit: cover; border-radius: 8px; border: 1px solid #e5e7eb;" onerror="this.src='${item.fallbackImage || item.image}'">
                  <div style="flex: 1;">
                    <div style="font-weight: 600; font-size: 0.9375rem; color: #000000;">${item.name}</div>
                    <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">Variant: ${item.variant || 'Silver'}</div>
                    <div style="font-weight: 600; font-size: 0.875rem; color: #000000; margin-top: 4px;">${formatRs(item.price)}</div>
                  </div>
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <div style="display: inline-flex; align-items: center; border: 1px solid #e5e7eb; border-radius: 6px;">
                      <button type="button" class="page-qty-minus" data-id="${item.id}" style="padding: 4px 8px; background: none; border: none; cursor: pointer; color: #4b5563;">−</button>
                      <span style="padding: 0 8px; font-weight: 600; font-size: 0.8125rem;">${item.quantity || 1}</span>
                      <button type="button" class="page-qty-plus" data-id="${item.id}" style="padding: 4px 8px; background: none; border: none; cursor: pointer; color: #4b5563;">+</button>
                    </div>
                    <button type="button" class="page-item-remove" data-id="${item.id}" style="background: none; border: none; color: #9ca3af; cursor: pointer; padding: 4px;" title="Remove">✕</button>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>

          <!-- Right: Order Summary & Promo Code Box -->
          <div style="background: #ffffff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 1.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05); position: sticky; top: 90px;">
            <h2 style="font-size: 1.125rem; font-weight: 600; margin-bottom: 1.25rem; border-bottom: 1px solid #f3f4f6; padding-bottom: 0.75rem;">Order Summary</h2>

            <!-- Promo Code Input & Apply Button -->
            <div style="margin-bottom: 1.5rem; background: #fafafa; border: 1px solid #f0f0f0; border-radius: 8px; padding: 1rem;">
              <label for="checkout-promo-input" style="display: block; font-size: 0.8125rem; font-weight: 600; color: #18181b; margin-bottom: 6px;">Promo Code</label>
              <form id="checkout-promo-form" style="display: flex; gap: 8px;" onsubmit="return false;">
                <input type="text" id="checkout-promo-input" placeholder="Enter promo code" style="flex: 1; height: 2.5rem; padding: 0 0.85rem; border: 1px solid #d4d4d8; border-radius: 6px; font-size: 0.8125rem; text-transform: uppercase; outline: none; background: #ffffff;" />
                <button type="submit" id="checkout-promo-btn" style="height: 2.5rem; padding: 0 1.25rem; background: #000000; color: #ffffff; border: none; border-radius: 6px; font-size: 0.8125rem; font-weight: 600; cursor: pointer;">
                  Apply
                </button>
              </form>
              <div id="checkout-promo-error" style="display: none; color: #dc2626; font-size: 0.75rem; margin-top: 6px; font-weight: 500;"></div>
              
              <!-- Applied Promo Pill -->
              ${coupon ? `
                <div style="display: inline-flex; align-items: center; gap: 8px; margin-top: 10px; padding: 5px 10px; background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 6px; font-size: 0.75rem; font-weight: 600; color: #047857;">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>
                  <span>${coupon.code}</span>
                  <span>(${coupon.type === 'flat' ? 'Rs.' + (coupon.value || 0) + ' OFF' : (coupon.value || coupon.discount || 0) + '% OFF'})</span>
                  <button type="button" id="checkout-remove-promo-btn" style="background: none; border: none; color: #047857; cursor: pointer; padding: 0 0 0 4px; font-weight: bold;" title="Remove promo code">✕</button>
                </div>
              ` : ''}
            </div>

            <!-- Price Breakdown -->
            <div style="display: flex; flex-direction: column; gap: 10px; border-bottom: 1px solid #f3f4f6; padding-bottom: 1.25rem; margin-bottom: 1.25rem;">
              <div style="display: flex; justify-content: space-between; font-size: 0.875rem; color: #4b5563;">
                <span>Subtotal</span>
                <span style="font-weight: 600; color: #000000;">${formatRs(rawSubtotal)}</span>
              </div>
              ${bundleDiscountAmount > 0 ? `
                <div style="display: flex; justify-content: space-between; font-size: 0.875rem; color: #16a34a; font-weight: 500;">
                  <span>Buy 2 &amp; Save Rs. 500</span>
                  <span style="font-weight: 600;">-Rs.500.00</span>
                </div>
              ` : ''}
              ${promoDiscountAmount > 0 ? `
                <div style="display: flex; justify-content: space-between; font-size: 0.875rem; color: #16a34a; font-weight: 500;">
                  <span>${promoLabel}</span>
                  <span style="font-weight: 600;">-${formatRs(promoDiscountAmount)}</span>
                </div>
              ` : ''}
              <div style="display: flex; justify-content: space-between; font-size: 0.875rem; color: #4b5563;">
                <span>Shipping</span>
                <span style="font-weight: 500; color: #16a34a;">Complimentary Luxury Courier</span>
              </div>
            </div>

            <!-- Total Price -->
            <div style="display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 1.5rem;">
              <span style="font-size: 1rem; font-weight: 600; color: #000000;">Estimated Total</span>
              <span style="font-size: 1.25rem; font-weight: 700; color: #000000;">${formatRs(finalTotal)} PKR</span>
            </div>

            <!-- Checkout CTAs -->
            <div style="display: flex; flex-direction: column; gap: 10px;">
              <button type="button" id="page-cod-btn" class="es-popup-button" style="height: 3rem; background: #000000; color: #ffffff; border: none; border-radius: 6px; font-weight: 600; font-size: 0.875rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="5" width="20" height="14" rx="2"></rect><line x1="2" y1="10" x2="22" y2="10"></line></svg>
                Place Order with Cash on Delivery
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    // Event listeners on page
    root.querySelectorAll('.page-qty-minus').forEach(btn => {
      btn.onclick = () => updateItemQuantity(btn.dataset.id, -1);
    });
    root.querySelectorAll('.page-qty-plus').forEach(btn => {
      btn.onclick = () => updateItemQuantity(btn.dataset.id, 1);
    });
    root.querySelectorAll('.page-item-remove').forEach(btn => {
      btn.onclick = () => removeFromCart(btn.dataset.id);
    });

    const promoForm = root.querySelector('#checkout-promo-form');
    const promoErr = root.querySelector('#checkout-promo-error');
    if (promoForm) {
      promoForm.onsubmit = async (e) => {
        e.preventDefault();
        const input = root.querySelector('#checkout-promo-input');
        const submitBtn = promoForm.querySelector('button[type="submit"]');
        const code = (input?.value || '').trim().toUpperCase();
        if (!code) return;

        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = '...';
        }

        try {
          const currentCart = getCart();
          const validation = await validateShopifyDiscount(code, currentCart);

          if (validation && validation.valid) {
            // Send live discount activation to Shopify session
            fetch('/discount/' + encodeURIComponent(code), { method: 'GET', credentials: 'same-origin' }).catch(() => {});

            if (promoErr) promoErr.style.display = 'none';

            saveCoupon({
              code: validation.code || code,
              type: validation.type || 'flat',
              value: typeof validation.value === 'number' ? validation.value : 0,
              description: validation.description || `Shopify Promo Code (${code})`
            });

            if (input) input.value = '';
            showToast('Promo Code Applied', `Code "${code}" applied successfully!`);
          } else {
            if (promoErr) {
              promoErr.style.display = 'block';
              promoErr.textContent = validation?.error || `Promo code "${code}" is invalid or inactive`;
            }
            showToast('Invalid Code', validation?.error || `Promo code "${code}" is not valid`, 'error');
          }
        } catch(err) {
          if (promoErr) {
            promoErr.style.display = 'block';
            promoErr.textContent = `Promo code "${code}" could not be verified`;
          }
        } finally {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Apply';
          }
        }
      };
    }

    const removePromoBtn = root.querySelector('#checkout-remove-promo-btn');
    if (removePromoBtn) {
      removePromoBtn.onclick = () => {
        saveCoupon(null);
        showToast('Promo code removed', 'Code was removed from your order');
      };
    }

    const pageCodBtn = root.querySelector('#page-cod-btn');
    if (pageCodBtn) {
      pageCodBtn.onclick = (e) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        proceedToShopifyCheckout(pageCodBtn);
      };
    }
  }

  // --- 6. QUICK ADD BINDINGS ---
  function initQuickAdd() {
    const buttons = document.querySelectorAll('button');
    buttons.forEach((btn) => {
      if (btn.textContent.includes('Quick Add') && !btn.dataset.thudarumBound) {
        btn.dataset.thudarumBound = 'true';

        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const card = btn.closest('.group') || btn.closest('a') || btn.parentElement;
          const nameEl = card ? (card.querySelector('h3') || card.querySelector('.font-medium')) : null;
          const priceEl = card ? card.querySelector('.font-medium.text-sm:last-child, p.text-sm.font-medium, [class*="font-medium"]') : null;
          const imgEl = card ? card.querySelector('img') : null;

          const name = nameEl ? nameEl.textContent.trim() : 'Luxury Watch';
          const priceMatch = (priceEl ? priceEl.textContent : '').match(/[0-9,]+/);
          const price = priceMatch ? Number(priceMatch[0].replace(/,/g, '')) : 2999;
          const image = imgEl ? (imgEl.getAttribute('src') || imgEl.src) : '';

          const originalContent = btn.innerHTML;
          btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>Added!`;
          btn.style.backgroundColor = '#16a34a';
          btn.style.color = '#ffffff';

          addToCart({
            id: 'watch-' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
            name,
            variant: 'Silver',
            price,
            originalPrice: Math.round(price * 1.6),
            image,
            quantity: 1
          }, true);

          setTimeout(() => {
            btn.innerHTML = originalContent;
            btn.style.backgroundColor = '';
            btn.style.color = '';
          }, 2000);
        });
      }
    });
  }

  // --- 7. HEADER CART TRIGGER ---
  function initHeaderCartTrigger() {
    const triggers = document.querySelectorAll('header button:has(svg.lucide-shopping-bag), [data-cart-trigger="true"], #header-cart-link, #header-cart-btn, header a[href*="cart"], a[href="#cart"]');
    triggers.forEach((trigger) => {
      if (trigger.dataset.sideCartBound) return;
      trigger.dataset.sideCartBound = 'true';

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openSideCart();
      });
    });

    if (!window._styluxCartDelegated) {
      window._styluxCartDelegated = true;
      document.addEventListener('click', (e) => {
        const cartEl = e.target.closest('#header-cart-btn, #header-cart-link, [data-cart-trigger="true"], a[href="#cart"], header a[href*="/cart"]');
        if (cartEl) {
          e.preventDefault();
          e.stopPropagation();
          openSideCart();
        }
      }, true);
    }
  }

  // --- 8. SMART HEADER ON SCROLL (Permanently Sticky) ---
  function initSmartHeader() {
    const header = document.querySelector('header');
    if (!header || header.dataset.smartHeaderInit) return;
    header.dataset.smartHeaderInit = 'true';

    header.style.position = 'sticky';
    header.style.top = '0';
    header.style.zIndex = '50';
    header.style.transform = 'none';
    header.style.willChange = 'auto';

    window.addEventListener('scroll', () => {
      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
      if (currentScrollY > 10) {
        header.style.boxShadow = '0 2px 10px rgba(0,0,0,0.05)';
      } else {
        header.style.boxShadow = 'none';
      }
    }, { passive: true });
  }

  // --- 9. INITIALIZATION ---
  function init() {
    ensureSideCartDom();
    initSmartHeader();
    initQuickAdd();
    initHeaderCartTrigger();
    syncCartWithShopify();
    updateCartUI();

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isSideCartOpen) {
        closeSideCart();
      }
    });

    document.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('#theme-drawer-close, .theme-drawer__close-button, [data-drawer-close]');
      if (closeBtn && closeBtn.closest('#stylux-side-cart-root')) {
        e.preventDefault();
        e.stopPropagation();
        closeSideCart();
      }
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  setTimeout(() => {
    initHeaderCartTrigger();
    initQuickAdd();
    syncCartWithShopify();
    updateCartUI();
  }, 150);

  setTimeout(() => {
    initHeaderCartTrigger();
    initQuickAdd();
    updateCartUI();
  }, 400);

  window.StyluxSideCart = {
    open: openSideCart,
    close: closeSideCart,
    addToCart,
    removeFromCart,
    updateItemQuantity,
    getCart,
    syncWithShopify: syncCartWithShopify,
    getDiscounts: getDiscountsDatabase,
    saveDiscounts: saveDiscountsDatabase,
    applyPromoCode: (code) => {
      const match = findDiscountByCode(code);
      if (match) {
        saveCoupon({
          code: match.code,
          type: match.type,
          value: match.value,
          description: match.description || ''
        });
        return { success: true, discount: match };
      }
      return { success: false, message: 'Invalid or inactive promo code' };
    },
    removePromoCode: () => {
      saveCoupon(null);
    },
    getAppliedPromo: getCoupon,
    checkout: proceedToShopifyCheckout
  };
})();
